<template>
  <div class="row">
    <BaseTextInput
      v-bind="form.input"
      v-bind:disabled="readonly"
      v-bind:label="label"
      v-bind:placeholder="placeholder"
      v-model="form.input.value"
      v-on:keydown.enter.prevent.self="submit"
    />
    <BaseSelect
      v-bind="form.tipo"
      v-bind:disabled="readonly"
      v-model="form.tipo.value"
      v-on:change="onFieldChange"
    />
    <BaseTypeahead
      v-show="isSearchByFilter"
      v-bind="form.cargo"
      v-bind:disabled="readonly"
      v-model="formattedCargo"
      v-on:keydown.enter.prevent.self="submit"
    />
    <BaseTypeahead
      v-show="isSearchByFilter"
      v-bind="form.localizacao"
      v-bind:disabled="readonly"
      v-model="formattedLocalizacao"
      v-on:keydown.enter.prevent.self="submit"
    />
    <BaseTypeahead
      v-show="isSearchByFilter"
      v-bind="form.lotacao"
      v-bind:disabled="readonly"
      v-model="formattedLotacao"
      v-on:keydown.enter.prevent.self="submit"
    />
    <div>
      <div class="col s12 m6">
        <BaseCheckbox
          v-show="isSearchByFilter"
          v-bind="form.hierarquiaCompleta"
          v-bind:disabled="readonly"
          v-model="form.hierarquiaCompleta.value"
          >Incluir Locais Subordinados</BaseCheckbox
        >
      </div>
      <div class="col s12 m6">
        <BaseCheckbox
          v-show="isSearchByFilter"
          v-bind="form.incluirInativos"
          v-bind:disabled="readonly"
          v-model="form.incluirInativos.value"
          >Incluir Inativos</BaseCheckbox
        >
      </div>
    </div>
    <div class="box-dropdown-funcional">
      <BaseSelect
        v-show="isSearchByFilter"
        :multiple="true"
        v-model="form.regimeJuridico.value"
        v-bind="form.regimeJuridico"
        v-bind:optionElements="regimesJuridicosOptions"
        >Regime Jurídico</BaseSelect
      >

      <BaseSelect
        v-show="isSearchByFilter"
        :multiple="true"
        v-model="form.situacaoFuncional.value"
        v-bind="form.situacaoFuncional"
        v-bind:optionElements="situacoesFuncionaisOptions"
        >Situação Funcional</BaseSelect
      >
    </div>
    <div class="col s12 right-align" v-if="linkLocalizacao || linkLotacao">
      <p>
        <a v-if="linkLocalizacao" v-bind:href="linkLocalizacao" target="_blank">
          Organograma Localização</a
        >
      </p>
      <p>
        <a v-if="linkLotacao" v-bind:href="linkLotacao" target="_blank">
          Organograma Lotação</a
        >
      </p>
    </div>
    <div class="col s12 buttons-row">
      <BaseButton v-bind="form.buttonClear" v-on:click="clear" />
      <BaseButton v-bind="form.buttonSubmit" v-on:click="submit" />
    </div>
  </div>
</template>

<script>
/**
 * @module components/BuscaServidorForm/BuscaServidorForm
 * @category Componentes
 * @summary _Single File Component_ (SFC) de busca de servidores.
 *
 * @description
 * Este componente disponibiliza um formulário para buscas de servidores por
 * opções de resultado único (por Siape ou CPF) e de múltiplos resultados
 * filtrando-se por nome e cargo.
 *
 * Este componente não possui _slot_ _default_.
 *
 * @requires module:constants.API_URL
 * @requires module:constants.DEFAULT_COLOR
 * @requires module:constants.DEFAULT_LUMINOSITY
 *
 * @vue-prop {string} [id] - Atributo `id` para identificação do componente.
 * Utilizado também para identificar unicamente cada campo do componente.
 * @vue-prop {boolean} [readonly] - Indica se o componente estará em modo
 * somente-leitura.
 * @vue-prop {object} [value={ tipo: "siape" }] - Valor inicial do componente.
 * Aceita as propriedades `tipo`, `siape`, `cpf`, `nome` e `cargo`. A primeira
 * propriedade pode assumir os valores "siape", "cpf" ou "filtros" indicando se a busca será por
 * matrícula Siape, por CPF ou a partir de filtros, respectivamente. As outras
 * propriedades fornecem os valores dos outros campos do formulário.
 * @vue-event {object} clear - Emite um indicativo de que os campos do
 * formulário (exceto o tipo de busca) foram limpos.
 * @vue-event {object} submit - Emite os dados preenchidos do formulário.
 *
 * @example
 * <BuscaServidorForm v-on:submit="buscaServidores" />
 */
import { API_URL } from "../../../constants";

const SEARCH_BY_SIAPE = "siape";
const SEARCH_BY_CPF = "cpf";
const SEARCH_BY_FILTROS = "filtros";

export default {
  name: "BuscaServidorForm",
  inheritAttrs: false,
  props: {
    readonly: Boolean,
    regimesJuridicos: Array,
    situacoesFuncionais: Array,

    value: {
      type: Object,
      default: () => ({
        tipo: SEARCH_BY_SIAPE,
      }),
    },
  },
  data() {
    return {
      loading: false,
      form: {
        input: {
          id: this.generateElementId(),
          name: "input",
          gridExpression: "col s12 m8",
          value: null,
        },
        tipo: {
          id: this.generateElementId(),
          name: "tipo",
          hasUnselectedOption: false,
          label: "Busca por",
          optionElements: [
            { value: SEARCH_BY_SIAPE, content: "Matrícula Siape" },
            { value: SEARCH_BY_CPF, content: "CPF" },
            { value: SEARCH_BY_FILTROS, content: "Filtros" },
          ],
          gridExpression: "col s12 m4",
          value: null,
        },
        cargo: {
          id: this.generateElementId(),
          name: "cargo",
          label: "Cargo",
          placeholder:
            "Informe um cargo através de seu nome (completo ou parte)",
          dropdownId: this.generateElementId(),
          searchFunction: this.searchCargo,
          gridExpression: "col s12",
          value: null,
        },
        incluirInativos: {
          id: this.generateElementId(),
          name: "incluirInativos",
          label: "Incluir Inativos",
          value: "true",
        },
        localizacao: {
          id: this.generateElementId(),
          name: "localizacao",
          label: "Localização",
          placeholder:
            "Informe uma localização através de seu nome (completo ou parte)",
          dropdownId: this.generateElementId(),
          searchFunction: this.searchLocalizacao,
          gridExpression: "col s12 xl6",
          value: null,
        },
        lotacao: {
          id: this.generateElementId(),
          name: "lotacao",
          label: "Lotação",
          placeholder:
            "Informe uma lotação através de seu nome (completo ou parte)",
          dropdownId: this.generateElementId(),
          searchFunction: this.searchLotacao,
          gridExpression: "col s12 xl6",
          value: null,
        },
        hierarquiaCompleta: {
          id: this.generateElementId(),
          name: "hierarquiaCompleta",
          value: "true",
        },
        regimeJuridico: {
          name: "Regime Jurídico",
          gridExpression: "col s12 m6",
          value: [],
        },
        situacaoFuncional: {
          name: "Situação Funcional",
          gridExpression: "col s12 m6",
          value: [],
        },
        buttonClear: {
          tag: "button",
          type: "button",
          content: "Limpar",
          iconName: "clear",
          title: "Limpar campos de busca",
        },
        buttonSubmit: {
          tag: "button",
          type: "button",
          content: "Buscar",
          iconName: "search",
          title: "Buscar servidores",
        },
      },
    };
  },
  computed: {
    label() {
      switch (this.form.tipo.value) {
        case SEARCH_BY_SIAPE:
          return "Matrícula Siape";
        case SEARCH_BY_CPF:
          return "CPF";
        case SEARCH_BY_FILTROS:
          return "Nome";
        default:
          return null;
      }
    },
    placeholder() {
      switch (this.form.tipo.value) {
        case SEARCH_BY_SIAPE:
          return "Informe uma matrícula Siape";
        case SEARCH_BY_CPF:
          return "Informe um CPF";
        case SEARCH_BY_FILTROS:
          return "Informe um nome (completo ou parte)";
        default:
          return null;
      }
    },
    formattedCargo: {
      get() {
        return this.form.cargo.value;
      },
      set(value) {
        this.form.cargo.value = value;
      },
    },
    formattedLocalizacao: {
      get() {
        return this.form.localizacao.value;
      },
      set(value) {
        this.form.localizacao.value = value;
      },
    },
    formattedLotacao: {
      get() {
        return this.form.lotacao.value;
      },
      set(value) {
        this.form.lotacao.value = value;
      },
    },
    isSearchByFilter() {
      return this.form.tipo.value === SEARCH_BY_FILTROS;
    },
    regimesJuridicosOptions() {
      return (this.regimesJuridicos || []).map((r) => ({
        value: r.codigo,
        content: `${r.codigo} - ${r.nome}`,
      }));
    },
    situacoesFuncionaisOptions() {
      return (this.situacoesFuncionais || []).map((r) => ({
        value: r.codigo,
        content: `${r.codigo} - ${r.nome}`,
      }));
    },
    linkLocalizacao() {
      if (!this.form.localizacao.value) return;
      return `${API_URL}/core/local/diagrama?organogramaCodigo=${this.form.localizacao.value.codigo}`;
    },
    linkLotacao() {
      if (!this.form.lotacao.value) return;
      return `${API_URL}/core/local/diagrama?organogramaCodigo=${this.form.lotacao.value.codigo}`;
    },
  },
  watch: {
    value: {
      immediate: true,

      handler(newValue) {
        const {
          input,
          tipo,
          incluirInativos,
          cargo,
          localizacao,
          lotacao,
          hierarquiaCompleta,
          situacaoFuncional,
          regimeJuridico,
        } = this.form;

        input.value = null;
        tipo.value = null;
        cargo.value = null;
        localizacao.value = null;
        lotacao.value = null;
        incluirInativos.value = true;
        hierarquiaCompleta.value = true;
        regimeJuridico.value = [];
        situacaoFuncional.value = [];

        if (newValue) {
          tipo.value = newValue.tipo;

          switch (newValue.tipo) {
            case SEARCH_BY_SIAPE:
              input.value = newValue.siape;
              break;
            case SEARCH_BY_CPF:
              input.value = newValue.cpf;
              break;
            case SEARCH_BY_FILTROS:
              input.value = newValue.nome;
              cargo.value = newValue.cargo;
              localizacao.value = newValue.localizacao;
              lotacao.value = newValue.lotacao;
              incluirInativos.value = newValue.incluirInativos;
              situacaoFuncional.value = newValue.situacaoFuncional;
              regimeJuridico.value = newValue.regimeJuridico;
              break;
            default:
              tipo.value = SEARCH_BY_SIAPE;

              this.$notification.pushWarning(
                "Tipo de busca inválido. Por padrão, a matrícula Siape foi escolhida"
              );
              break;
          }
        } else {
          tipo.value = SEARCH_BY_SIAPE;

          this.$notification.pushWarning(
            "Tipo de busca inválido. Por padrão, a matrícula Siape foi escolhida"
          );
        }
      },
    },
  },
  mounted() {
    this.focusSearchElement();
  },
  methods: {
    generateElementId() {
      return this.$utils.Object.uuid();
    },
    getLocal(value) {
      return this.$http
        .get(`${API_URL}/core/local?busca=${value}`)
        .then((response) => response.data.elementos || [])
        .then((locais) =>
          locais.map((item) => ({
            codigo: item.codigo,
            content: item.sigla
              ? `${item.codigo} - ${item.nome} - ${item.sigla}`
              : `${item.codigo} - ${item.nome}`,
          }))
        )
        .catch((error) =>
          this.$notification.pushError(
            `Houve um erro ao realizar a busca de Local: ${error.message}`
          )
        );
    },
    searchCargo(value) {
      return this.$http
        .get(`${API_URL}/core/cargo?nome=${value}`)
        .then((response) => response.data.elementos || [])
        .then((cargos) =>
          cargos.map((item) => ({
            codigo: item.codigo,
            content: `${item.codigo} - ${item.nome}`,
          }))
        )
        .catch((error) =>
          this.$notification.pushError(
            `Houve um erro ao realizar a busca de Cargo: ${error.message}`
          )
        );
    },
    searchLocalizacao(value) {
      return this.getLocal(value);
    },
    searchLotacao(value) {
      return this.getLocal(value);
    },
    focusSearchElement() {
      let elemId = this.form.input.id;

      this.$nextTick().then(() => {
        const elem = document.getElementById(elemId);

        if (elem) {
          elem.focus();
        }
      });
    },
    onFieldChange() {
      // Limpa os campos de busca
      this.form.input.value = null;
      this.form.cargo.value = null;
      this.form.localizacao.value = null;
      this.form.lotacao.value = null;

      this.focusSearchElement();
    },
    clear() {
      const {
        input,
        cargo,
        incluirInativos,
        localizacao,
        lotacao,
        hierarquiaCompleta,
        regimeJuridico,
        situacaoFuncional,
      } = this.form;

      input.value = null;
      cargo.value = null;
      hierarquiaCompleta.value = true;
      localizacao.value = null;
      lotacao.value = null;
      incluirInativos.value = true;
      regimeJuridico.value = [];
      situacaoFuncional.value = [];

      this.$emit("clear");
    },
    submit() {
      const {
        input,
        tipo,
        cargo,
        incluirInativos,
        localizacao,
        lotacao,
        hierarquiaCompleta,
        regimeJuridico,
        situacaoFuncional,
      } = this.form;

      const value = {
        tipo: tipo.value,
        siape: null,
        cpf: null,
        nome: null,
        incluirInativos: null,
        cargo: null,
        localizacao: null,
        lotacao: null,
        hierarquiaCompleta: null,
        regimeJuridico: null,
        situacaoFuncional: null,
      };

      switch (tipo.value) {
        case SEARCH_BY_SIAPE:
          value.siape = input.value;
          break;
        case SEARCH_BY_CPF:
          value.cpf = input.value;
          break;
        case SEARCH_BY_FILTROS:
          value.nome = input.value ? input.value : null;
          value.cargo = cargo.value ? cargo.value.codigo : null;
          value.localizacao = localizacao.value
            ? localizacao.value.codigo
            : null;
          value.lotacao = lotacao.value?.codigo;
          value.incluirInativos = incluirInativos.value;
          value.hierarquiaCompleta = hierarquiaCompleta.value;
          value.regimeJuridico = (regimeJuridico.value || []).join(",");
          value.situacaoFuncional = (situacaoFuncional.value || []).join(",");
          break;
        default:
          break;
      }

      this.$emit("submit", value);
    },
  },
};
</script>
<style scoped>
.box-dropdown-funcional {
  margin-top: 20px;
  display: inline-block;
  width: 100%;
}
</style>
